import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { buildOrGroupsForSearch } from '@/hooks/useProperties';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface AddressSuggestion {
  id: string;
  slug: string | null;
  address: string;
  city: string;
  neighborhood: string | null;
}

/**
 * Fetches address suggestions from the properties table based on user input.
 * Only triggers when the query is at least 3 characters.
 */
export function useAddressSuggestions(query: string) {
  // Debounce so we hit the database once the visitor pauses, not per keystroke.
  const normalizedQuery = useDebouncedValue(query.replace(/\s+/g, ' ').trim(), 250);
  
  
  return useQuery({
    queryKey: ['address-suggestions', normalizedQuery],
    queryFn: async () => {
      if (normalizedQuery.length < 3) return [];
      
      // Search addresses using the same token/abbreviation logic as results
      let request = supabase
        .from('properties')
        .select('id, slug, address, city, neighborhood')
        .eq('status', 'Active')
        .order('listing_date', { ascending: false })
        .limit(8);

      for (const group of buildOrGroupsForSearch(normalizedQuery)) {
        request = request.or(group);
      }

      const { data, error } = await request;
      
      if (error) throw error;
      return data as AddressSuggestion[];
    },
    enabled: normalizedQuery.length >= 3,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    gcTime: 1000 * 60 * 5,
  });
}
